import { AppDialogContentText } from "@/components/ui/app-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

// The donor's shared "are you sure" — `timmy/src/components/confirm-delete-dialog/
// confirm-delete-dialog.tsx`.
//
// **Here rather than in the feature folder because it is shared in the source**
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components"): the donor parameterises it by `label` and calls it
// from several delete islands, which is exactly why its copy reads "Delete
// {label}" and "delete the {label}". Reproducing that sentence per screen is
// how two screens end up asking the question two different ways.
//
// It reports no failure of its own: the donor's islands close this dialog on
// success and leave it open on failure, putting the error in a dismissible
// snackbar beside it (`ItemDeleteIsland.tsx:76-81`) — so the retry the copy
// promises is the Delete button still on screen, and the caller owns the
// message.
//
// It is a preset of the shared `ConfirmDialog` — `AppDialog`, itself the port of
// the donor's shared `Dialog`, with the Cancel-and-verb footer — so the chrome,
// the button shapes and the 600px paper all come from one place. The *batch*
// confirmations (`catalog-batch-dialogs.tsx`) share that frame but not this
// preset: they are a different donor component (`delete-item-dialog.tsx`,
// which lists the selection), so their body is their own.

interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The noun, lower case — "item", "line". The donor's own prop, and it lands
   * in both the title and the question.
   */
  label: string;
  /**
   * An extra clause between the question and the warning, as the donor has —
   * the line delete's cascade sentence (#807).
   */
  description?: string;
  onConfirm: () => void;
  isDeleting?: boolean;
  /**
   * Open on Delete — the donor's `autoFocus` (`confirm-delete-dialog.tsx:44`).
   * On by default; the Editor's group delete opens on its paper instead, as
   * measured on the running donor (#805 enumeration E4), and passes `false`.
   */
  autoFocusConfirm?: boolean;
  /**
   * MUI's `fullWidth`. On by default for today's callers; the donor's dialog
   * passes none, and the Editor's group delete is content-sized as measured
   * (#805 enumeration E4).
   */
  fullWidth?: boolean;
  /** The paper has gone — the donor's `onExited`. */
  onExited?: () => void;
}

export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  label,
  description,
  onConfirm,
  isDeleting = false,
  autoFocusConfirm = true,
  fullWidth = true,
  onExited,
}: ConfirmDeleteDialogProps) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Delete ${label}`}
      confirmLabel="Delete"
      onConfirm={onConfirm}
      isSubmitting={isDeleting}
      // Dismissal stays live while the delete is in flight, as the donor has it
      // (`confirm-delete-dialog.tsx:26` passes `onClose` unconditionally):
      // only the two buttons take the `isDeleting` gate. Escape, the overlay
      // and the corner X close the dialog mid-write, and the outcome reaches
      // the user in the caller's snackbar either way.
      dismissibleWhileSubmitting
      // `autoFocus` is the donor's (`confirm-delete-dialog.tsx:44`): the
      // destructive button is the one the dialog opens on.
      autoFocusConfirm={autoFocusConfirm}
      fullWidth={fullWidth}
      onCloseAutoFocus={onExited ? () => onExited() : undefined}
      // `confirm-delete-dialog.tsx:26-30` — the donor names its body as the
      // dialog's description, so a screen reader reads the question with the
      // title rather than only on arrival at the paragraph.
      describedById="alert-dialog-description"
      cancelTestId="delete-option-dialog-cancel"
      confirmTestId="delete-option-dialog-confirm"
    >
      {/* The donor's `<br />` between the question and the warning, kept: the
          two are separate statements and it reads as two lines on the screen
          being ported. Between the question and the description, the donor's
          `?&nbsp;` (`confirm-delete-dialog.tsx:30-31`) — a non-breaking space,
          so a wrap never strands the description's first word away from the
          `?` — written whether or not a description follows (#807 E23). */}
      <AppDialogContentText
        id="alert-dialog-description"
        data-testid="confirm-delete-dialog-content"
      >
        Are you sure that you want to delete the {label}?{"\u00a0"}
        {description}
        <br />
        This action cannot be undone.
      </AppDialogContentText>
    </ConfirmDialog>
  );
}
